/**
 * Maintenance mode (+Business.AppWrapper/specs/maintenance): one business's access put under maintenance on every
 * channel, or the desktop or mobile app put under maintenance platform-wide. Served by the account API's
 * api/admin/maintenance; the apps read api/maintenance.
 */
export type MaintenanceChannel = 'desktop' | 'mobile';

/** A platform channel that is under maintenance, as stored. Null on the overview means it is open. */
export interface MaintenanceNotice {
  message: string | null;
  expectedEndUtc: string | null;
  startedAtUtc: string;
  startedBy: string | null;
}

/** A business under maintenance. */
export interface BusinessMaintenance {
  businessId: string;
  businessName: string | null;
  message: string | null;
  expectedEndUtc: string | null;
  startedAtUtc: string;
  startedBy: string | null;
}

export interface MaintenanceOverview {
  desktop: MaintenanceNotice | null;
  mobile: MaintenanceNotice | null;
  businesses: BusinessMaintenance[];
}

/** Body of a PUT: the words the apps show (blank = their default wording) and when staff expect to be done. */
export interface MaintenanceRequest {
  message?: string | null;
  expectedEndUtc?: string | null;
}

export interface MaintenanceChannelCopy {
  key: MaintenanceChannel;
  name: string;
  covers: string;
  /** What putting it under maintenance does, for the question. */
  whenClosed: string;
}

export const MAINTENANCE_CHANNELS: readonly MaintenanceChannelCopy[] = [
  {
    key: 'desktop',
    name: 'Desktop app',
    covers: 'The installed +Business desktop app on every till, for every business.',
    whenClosed: 'Every till sees "Platform under maintenance" and cannot trade until you lift it. The browser account site stays open, so billing can still be managed.'
  },
  {
    key: 'mobile',
    name: 'Mobile app',
    covers: 'The Plus mobile app on every phone, for every business.',
    whenClosed: 'Every phone sees "Platform under maintenance" and cannot trade until you lift it.'
  }
];

/** The longest message the API keeps. */
export const MAINTENANCE_MESSAGE_MAX = 500;

/** The words the apps use when staff leave the message blank; shown as the placeholder so staff know what merchants read. */
export const MAINTENANCE_DEFAULT_MESSAGE =
  'We’re carrying out some maintenance right now. Nothing has been lost, and you’ll be able to carry on as soon as it’s finished.';
